import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { taskKeys } from '@/lib/queryKeys';
import { taskEndpoints } from '@/lib/api/endpoints';
import { apiPost, operationsClient } from '@/lib/api';
import type { Task, UpdateTaskStatusDto, TaskListResponse } from '@/types/task';

export function useUpdateTaskStatus() {
  const queryClient = useQueryClient();

  return useApiMutation<Task, UpdateTaskStatusDto>({
    mutationFn: ({ id, status }) =>
      apiPost<Task, { status: string }>(operationsClient, taskEndpoints.updateStatus(id), {
        status,
      }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.all() });

      // Snapshot all list caches for rollback
      const listSnapshots = new Map<string, TaskListResponse>();
      queryClient
        .getQueriesData<TaskListResponse>({ queryKey: taskKeys.lists() })
        .forEach(([key, data]) => {
          if (data) {
            listSnapshots.set(JSON.stringify(key), data);
            queryClient.setQueryData<TaskListResponse>(key as string[], {
              ...data,
              tasks: data.tasks.map((t) => (t.id === id ? { ...t, status } : t)),
            });
          }
        });

      // Snapshot detail cache
      const previousDetail = queryClient.getQueryData<Task>(taskKeys.detail(id));
      if (previousDetail) {
        queryClient.setQueryData<Task>(taskKeys.detail(id), { ...previousDetail, status });
      }

      return { listSnapshots, previousDetail };
    },
    onError: (_err, { id }, context) => {
      const ctx = context as
        | {
            listSnapshots?: Map<string, TaskListResponse>;
            previousDetail?: Task;
          }
        | undefined;

      ctx?.listSnapshots?.forEach((data, keyStr) => {
        queryClient.setQueryData(JSON.parse(keyStr) as string[], data);
      });
      if (ctx?.previousDetail) {
        queryClient.setQueryData(taskKeys.detail(id), ctx.previousDetail);
      }
      toast.error('Failed to update status — reverted');
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: taskKeys.countsAll() });
      void queryClient.invalidateQueries({ queryKey: taskKeys.detail(data.id) });
    },
  });
}
